// The live profile (§7.4, §27.3): one per page. Owns persistence, the 'profile' bus event, daily
// challenges, match rewards, the shop and unlock gating. See the contract in ./Profile.ts.
import { MAX_BEANS, NO_SLOT, TUNE } from '@shared/constants';
import type { CatalogItem, CosmeticCategory } from '@shared/cosmetics';
import { ModeId, type MapId } from '@shared/ids';
import { cleanName } from '@shared/names';
import type { MatchEndDTO, RoundEndDTO, RoundInitDTO } from '@shared/protocol/messages';
import { onSim, type Bus } from '../core/bus';
import { CHALLENGES, ChallengeTracker, dailyChallengeIds, utcDate } from './challenges';
import { createProfile, loadProfile, resetProgression, sanitizeProfile, saveProfile, type Profile } from './Profile';
import {
  addXp, buyItem, equipItem, itemStatus, levelInfo, lockedEquipFix, matchBucks, matchXpLines, shopForDate, unlocksBetween,
  type BuyResult, type ItemStatus, type LevelInfo, type MatchLedger, type MatchRewards,
} from './progression';

export interface ChallengeView { id: number; text: string; unit: string; progress: number; goal: number; done: boolean; xp: number; bucks: number }
export interface ShopSlot { item: CatalogItem; status: ItemStatus; affordable: boolean }

export interface ProfileService {
  get(): Profile;
  set(p: Profile): void;
  setName(name: string): boolean;
  equip(cat: CosmeticCategory, id: number): boolean;
  setIdentityPref(i: number): boolean;
  buy(cat: CosmeticCategory, id: number): BuyResult;
  level(): LevelInfo;
  itemStatus(cat: CosmeticCategory, id: number): ItemStatus;
  shop(): ShopSlot[];
  challenges(): ChallengeView[];
  lastRewards(): MatchRewards | null;
  onRewards(fn: (r: MatchRewards) => void): () => void;
  setPostcard(dataUrl: string): void;
  /** Records first-time prompt progress (used by ./prompts). */
  promptUse(id: string, goal: number): void;
  resetProgress(): void;
  /** Writes any pending change to storage now. */
  flush(): void;
  dispose(): void;
}

export interface ProfileServiceOptions {
  bus: Bus;
  /** Starting profile (defaults to the stored one). */
  initial?: Profile;
  /** Clock for the UTC date (tests). */
  now?: () => Date;
  /** Count this page load as a session (default true). */
  countSession?: boolean;
}

/** Progress writes are batched; explicit actions save at once. */
const SAVE_DEBOUNCE_MS = 600;

export function createProfileService(opts: ProfileServiceOptions): ProfileService {
  const bus = opts.bus;
  const clock = opts.now ?? (() => new Date());
  let profile = opts.initial ? sanitizeProfile(opts.initial) : loadProfile();
  if (opts.countSession !== false) profile = { ...profile, sessions: profile.sessions + 1 };
  profile = lockedEquipFix(ensureDaily(profile, utcDate(clock())));
  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  let rewards: MatchRewards | null = null;
  const rewardListeners = new Set<(r: MatchRewards) => void>();
  const offs: (() => void)[] = [];

  // Online match state.
  let localSlot: number = NO_SLOT;
  let spectator = false;
  let inMatch = false;
  let snapshot = { level: profile.level, xp: profile.xp };
  let ledger: MatchLedger = newLedger();
  let roundMap: MapId | null = null;
  const roundMaps: MapId[] = [];

  const tracker = new ChallengeTracker((id, delta, absolute) => progress(id, delta, absolute));

  function newLedger(): MatchLedger {
    return { localSlot, team: 0, roundPlaces: [], challengesCompleted: [] };
  }

  function persistSoon(): void {
    if (saveTimer !== null) return;
    saveTimer = setTimeout(() => {
      saveTimer = null;
      saveProfile(profile);
    }, SAVE_DEBOUNCE_MS);
  }

  function flush(): void {
    if (saveTimer !== null) clearTimeout(saveTimer);
    saveTimer = null;
    saveProfile(profile);
  }

  /** Replaces the profile, persists and announces it. */
  function commit(next: Profile, immediate = true): void {
    profile = next;
    if (immediate) flush();
    else persistSoon();
    bus.emit('profile', profile);
  }

  function today(): string {
    return utcDate(clock());
  }

  /** Rolls the daily challenges over at UTC midnight (§7.4). */
  function rollover(): void {
    const next = ensureDaily(profile, today());
    if (next !== profile) commit(next);
  }

  // ---- Challenges -----------------------------------------------------------------------------

  function progress(id: number, delta: number, absolute?: number): void {
    rollover();
    const ch = profile.challenges;
    const i = ch.ids.indexOf(id);
    if (i < 0 || ch.done[i]) return;
    const def = CHALLENGES[id];
    const before = ch.progress[i] ?? 0;
    const value = Math.min(def.goal, absolute !== undefined ? Math.max(before, absolute) : before + delta);
    if (value === before) return;
    const done = value >= def.goal;
    const challenges = { ...ch, progress: ch.progress.map((v, k) => (k === i ? value : v)), done: ch.done.map((d, k) => (k === i ? done : d)) };
    let next: Profile = { ...profile, challenges };
    if (done) {
      // Each challenge pays 150 XP and 50 Bucks on completion; the match summary lists it.
      const c = TUNE.progression.challenges;
      const xp = addXp(next.level, next.xp, c.xp);
      next = { ...next, level: xp.level, xp: xp.xp, totalXp: next.totalXp + c.xp, bucks: next.bucks + c.bucks + xp.capBonusBucks };
      if (inMatch) ledger.challengesCompleted.push(id);
    }
    commit(next, done);
    bus.emit('challenge:progress', { id, progress: value, goal: def.goal, done });
    if (done) bus.emit('toast', { text: `Challenge complete: ${def.text}! +${TUNE.progression.challenges.xp} XP`, kind: 'challenge' });
  }

  // ---- Online match flow ----------------------------------------------------------------------

  function onRoundInit(init: RoundInitDTO): void {
    const scoring = init.cfg.modeId !== ModeId.LOBBY && init.cfg.modeId !== ModeId.PODIUM;
    if (!scoring || spectator || localSlot === NO_SLOT || !init.cfg.activeSlots[localSlot]) {
      tracker.beginRound(null);
      roundMap = null;
      return;
    }
    if (!inMatch) {
      inMatch = true;
      snapshot = { level: profile.level, xp: profile.xp };
      ledger = newLedger();
      roundMaps.length = 0;
      tracker.beginMatch();
    }
    const team = init.cfg.teams[localSlot] ?? 0;
    ledger.team = team;
    roundMap = init.cfg.mapId;
    tracker.beginRound({ mapId: init.cfg.mapId, modeId: init.cfg.modeId, golden: init.cfg.golden, team });
  }

  function onRoundEnd(end: RoundEndDTO): void {
    if (!inMatch || !tracker.active) return;
    const mine = end.results.find((r) => r.slot === localSlot);
    const place = mine?.place ?? 0;
    const won = end.winner === localSlot || (end.winner === 254 && ledger.team !== 0 && end.winningTeam === ledger.team);
    if (place > 0) ledger.roundPlaces.push(place);
    if (roundMap !== null) roundMaps.push(roundMap);
    tracker.roundEnd(place, won);
    tracker.beginRound(null);
  }

  function onMatchEnd(end: MatchEndDTO): void {
    if (!inMatch || localSlot === NO_SLOT) return;
    tracker.reconcile(end.stats[localSlot]);
    inMatch = false;
    const { lines, kos, won, place } = matchXpLines(end, { ...ledger, localSlot });
    // Challenge XP was granted on completion; add the rest now.
    const chXp = lines.find((l) => l.id === 'challenges')?.xp ?? 0;
    const matchXp = lines.reduce((s, l) => s + l.xp, 0);
    const gain = matchXp - chXp;
    const xp = addXp(profile.level, profile.xp, gain);
    const bucksMatch = matchBucks(kos, won);
    const st = end.stats[localSlot];
    const stats = { ...profile.stats, mapRounds: [...profile.stats.mapRounds] };
    stats.matches++;
    if (won) stats.wins++;
    stats.rounds += ledger.roundPlaces.length;
    stats.kos += kos;
    if (st) {
      stats.clutches += st.clutches;
      stats.longestFlight = Math.max(stats.longestFlight, st.longestFlight);
      stats.oldestPeelTicks = Math.max(stats.oldestPeelTicks, st.oldestPeelKoTicks);
    }
    for (const m of roundMaps) if (m >= 0 && m < stats.mapRounds.length) stats.mapRounds[m]++;
    const next: Profile = {
      ...profile, level: xp.level, xp: xp.xp, totalXp: profile.totalXp + gain, bucks: profile.bucks + bucksMatch + xp.capBonusBucks, stats,
    };
    const chCount = ledger.challengesCompleted.length;
    rewards = {
      lines, xp: matchXp, bucks: bucksMatch + chCount * TUNE.progression.challenges.bucks + xp.capBonusBucks,
      bucksMatch, bucksChallenges: chCount * TUNE.progression.challenges.bucks, bucksCapBonus: xp.capBonusBucks,
      kos, won, place,
      levelBefore: snapshot.level, xpBefore: snapshot.xp, levelAfter: xp.level, xpAfter: xp.xp,
      unlocks: unlocksBetween(snapshot.level, xp.level), challengesCompleted: [...ledger.challengesCompleted],
    };
    commit(next);
    if (rewards.levelAfter > rewards.levelBefore) bus.emit('toast', { text: `LEVEL UP! Bean Level ${rewards.levelAfter}`, kind: 'levelup' });
    for (const fn of rewardListeners) fn(rewards);
  }

  offs.push(bus.on('joined', (j) => {
    if (j.slot !== localSlot) inMatch = false;
    localSlot = j.slot;
    spectator = j.spectator;
  }));
  offs.push(bus.on('round:init', onRoundInit));
  offs.push(bus.on('round:end', onRoundEnd));
  offs.push(bus.on('match:end', onMatchEnd));
  offs.push(bus.on('ui:screen', (id) => {
    // Back in the main menu: the room is gone, nothing more counts until the next round:init.
    if (id === 'menu') {
      inMatch = false;
      tracker.beginRound(null);
      localSlot = NO_SLOT;
    }
  }));
  offs.push(onSim(bus, (ev, _local, replay) => {
    if (replay || !tracker.active || localSlot === NO_SLOT || localSlot >= MAX_BEANS) return;
    tracker.event(ev, localSlot);
  }));

  if (typeof window !== 'undefined') {
    const onHide = (): void => {
      if (saveTimer !== null) flush();
    };
    window.addEventListener('pagehide', onHide);
    offs.push(() => window.removeEventListener('pagehide', onHide));
  }

  flush();

  return {
    get: () => profile,
    set(p) {
      commit(lockedEquipFix(sanitizeProfile(p)));
    },
    setName(name) {
      const clean = cleanName(name);
      if (clean === null) return false;
      if (clean !== profile.name) commit({ ...profile, name: clean });
      return true;
    },
    equip(cat, id) {
      const next = equipItem(profile, cat, id);
      if (!next) return false;
      if (next !== profile) commit(next);
      return true;
    },
    setIdentityPref(i) {
      const next = equipItem(profile, 'identityPref', i);
      if (!next) return false;
      if (next !== profile) commit(next);
      return true;
    },
    buy(cat, id) {
      const r = buyItem(profile, cat, id, today());
      if (r.result === 'ok') commit(r.profile);
      return r.result;
    },
    level: () => levelInfo(profile),
    itemStatus: (cat, id) => itemStatus(profile, cat, id, today()),
    shop() {
      const date = today();
      return shopForDate(date).map((item) => {
        const status = itemStatus(profile, item.cat, item.id, date);
        return { item, status, affordable: profile.bucks >= item.price };
      });
    },
    challenges() {
      rollover();
      const ch = profile.challenges;
      const c = TUNE.progression.challenges;
      return ch.ids.map((id, i) => {
        const def = CHALLENGES[id];
        return { id, text: def.text, unit: def.unit, progress: Math.min(def.goal, ch.progress[i] ?? 0), goal: def.goal, done: ch.done[i] === true, xp: c.xp, bucks: c.bucks };
      });
    },
    lastRewards: () => rewards,
    onRewards(fn) {
      rewardListeners.add(fn);
      return () => {
        rewardListeners.delete(fn);
      };
    },
    setPostcard(dataUrl) {
      if (!dataUrl.startsWith('data:image/png')) return;
      commit({ ...profile, lastPostcard: dataUrl });
    },
    promptUse(id, goal) {
      const uses = Math.min(goal, (profile.promptUses[id] ?? 0) + 1);
      const done = uses >= goal && !profile.firstPromptsDone.includes(id);
      commit({
        ...profile, promptUses: { ...profile.promptUses, [id]: uses },
        firstPromptsDone: done ? [...profile.firstPromptsDone, id] : profile.firstPromptsDone,
      }, done);
    },
    resetProgress() {
      inMatch = false;
      rewards = null;
      commit(lockedEquipFix(ensureDaily(resetProgression(profile), today())));
    },
    flush,
    dispose() {
      flush();
      for (const off of offs) off();
    },
  };
}

/** Draws today's challenges when the stored ones are from another UTC day (§7.4). */
export function ensureDaily(p: Profile, date: string): Profile {
  if (p.challenges.date === date && p.challenges.ids.length === TUNE.progression.challenges.perDay) return p;
  const ids = dailyChallengeIds(date);
  return { ...p, challenges: { date, ids, progress: ids.map(() => 0), done: ids.map(() => false) } };
}

export { createProfile };
export type { BuyResult, ItemStatus, LevelInfo, MatchRewards };
